/**
 * Consulta ao backend "existe conta com esse e-mail?" — usado pelas telas de
 * login e de redefinição de senha para mostrar mensagens claras ("não existe
 * conta com esse e-mail" x "senha incorreta").
 *
 * REGRA DE OURO: falha SEMPRE "aberta". Qualquer problema (sem URL configurada,
 * identificador que não é e-mail, timeout, rede, status != 2xx, corpo
 * inesperado) retorna `null` = "não sei" → o chamador segue o comportamento
 * genérico atual e NUNCA bloqueia um usuário legítimo. Só retorna `boolean`
 * quando o backend responde exatamente `{ exists: boolean }`.
 */

import { API_BASE_URL } from "@/config/env";

// Regex simples só pra evitar chamar o backend com telefone/usuário.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_TIMEOUT_MS = 2500;

export interface CheckEmailExistsOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * @returns `true`/`false` quando o backend responde; `null` quando não foi
 * possível determinar (fail-open).
 */
export async function checkEmailExists(
  rawEmail: string,
  opts: CheckEmailExistsOptions = {}
): Promise<boolean | null> {
  if (!API_BASE_URL) {
    return null;
  }

  const email = (rawEmail || "").trim().toLowerCase();
  if (!EMAIL_RE.test(email)) {
    return null;
  }

  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    opts.timeoutMs ?? DEFAULT_TIMEOUT_MS
  );

  // Aborta se o caller cancelar (ex.: componente desmontou).
  if (opts.signal) {
    if (opts.signal.aborted) {
      clearTimeout(timeout);
      return null;
    }
    opts.signal.addEventListener("abort", () => controller.abort(), {
      once: true,
    });
  }

  try {
    const res = await fetch(`${API_BASE_URL}/auth/email-exists`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      credentials: "omit",
      signal: controller.signal,
    });

    if (!res.ok) {
      return null;
    }

    const data: unknown = await res.json();
    if (
      data &&
      typeof data === "object" &&
      typeof (data as { exists?: unknown }).exists === "boolean"
    ) {
      return (data as { exists: boolean }).exists;
    }
    return null;
  } catch {
    // timeout, abort, rede, JSON inválido — tudo cai em fail-open.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
